import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * HelpTip — inline `?` icon next to a form field/label that opens a
 * compact popover with an explanation and (optionally) a "Read more →"
 * link to a full Help article.
 *
 * Industry pattern: Stripe, Salesforce, and Linear all expose tooltips
 * exactly like this. Keep the `short` text under ~140 chars; longer
 * material belongs in a Help article.
 *
 * Usage:
 *   <HelpTip topic="ticket.priority" />
 *   <Field label={<span>Priority <HelpTip topic="ticket.priority" /></span>}>...
 *
 * Falls back silently if `topic` is unknown — we'd rather a missing
 * registry entry render nothing than crash the form.
 */
import { HelpCircle } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { HELP_TOPICS } from '@/content/help/topics';
import { cn } from '@/lib/cn';
export function HelpTip({ topic, size = 13, className }) {
    const entry = HELP_TOPICS[topic];
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const labelId = useId();
    // Close on outside click or Escape — standard popover hygiene.
    useEffect(() => {
        if (!open)
            return;
        const onDown = (e) => {
            if (!wrapRef.current?.contains(e.target))
                setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === 'Escape')
                setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);
    if (!entry)
        return null;
    return (_jsxs("span", { ref: wrapRef, className: "relative inline-flex", children: [_jsx("button", { type: "button", "aria-label": "Help", "aria-expanded": open, "aria-describedby": open ? labelId : undefined, onClick: (e) => {
                    // Prevent label-click toggling the underlying input.
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen((o) => !o);
                }, className: cn('ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300', className), children: _jsx(HelpCircle, { size: size }) }), open && (_jsxs("span", { role: "tooltip", id: labelId, className: "absolute left-0 top-5 z-50 w-64 rounded-md border border-slate-200 bg-white p-3 text-[12px] leading-relaxed text-slate-700 shadow-lg dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200", children: [_jsx("span", { className: "block", children: entry.short }), entry.articleSlug && (_jsx(Link, { to: `/help/articles/${entry.articleSlug}`, className: "mt-2 inline-flex text-[11px] font-medium text-brand-600 hover:underline", onClick: () => setOpen(false), children: "Read more \u2192" }))] }))] }));
}
